import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { styled } from "@mui/material/styles";
import BryxColors from "../types/theme";
import { TimePicker } from "@mui/x-date-pickers-pro";
import { Stack, SvgIcon } from "@mui/material";
import BryxInput from "./BryxInput";
import dayjs from "dayjs";
import BryxTimeSubInput from "./BryxTimeSubInput";
import withStaticMode from "../hocs/withStaticMode";
const BryxTimeInput = withStaticMode(styled(({ value, onChange, error, errorText, ...rest }) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const palette = "light";
    const hoverColor = BryxColors.brand1(palette);
    const activeColor = palette == "light" ? BryxColors.brandDark2(palette) : BryxColors.brand4(palette);
    const inactiveColor = BryxColors.notify(palette, "inactive");
    const validationError = value == null ? false : !value.isValid();
    const labelColor = validationError
        ? BryxColors.notify(palette, "error")
        : isFocused
            ? `${activeColor} !important`
            : isHovered
                ? `${hoverColor} !important`
                : inactiveColor;
    //if error, error. If focused on text, show focused;
    const inputColor = validationError ? BryxColors.notify(palette, "error") : isHovered ? `${hoverColor} !important` : undefined;
    const formControlStyling = {
        paddingTop: "2.5rem",
        maxWidth: "200px",
        "& *": {
            color: inputColor,
            fill: inputColor,
            borderColor: inputColor,
            "& ::placeholder": {
                color: inputColor,
            },
        },
    };
    const labelStyling = {
        marginTop: "1rem",
        marginLeft: "-0.75rem",
        fontSize: "larger",
        color: labelColor,
    };
    const colonStyling = {
        marginTop: "auto",
        marginBottom: "auto",
        paddingBottom: "8px",
        marginLeft: "-6px",
    };
    const changeHook = (time) => {
        if (time.lastIndexOf(":") != 5) {
            return;
        }
        const formattedValue = value || dayjs();
        formattedValue.set("hour", parseInt(time.substring(0, 2), 10));
        formattedValue.set("minute", parseInt(time.substring(3, 5), 10));
        formattedValue.set("second", parseInt(time.substring(6, 8), 10));
        onChange(formattedValue);
    };
    return (_jsx(TimePicker, { ...rest, onChange: onChange, value: value, OpenPickerButtonProps: {
            sx: {
                padding: "4px",
            },
        }, PopperProps: {
            sx: {
                paddingLeft: "1rem !important",
                paddingTop: "1.5rem !important",
            },
        }, PaperProps: {
            sx: {
                padding: "16px 0 16px 16px",
                maxHeight: "100px",
            },
        }, components: {
            OpenPickerIcon: () => (_jsx(SvgIcon, { viewBox: "-3.5 -3.5 24 24", children: _jsx("path", { d: "M8.99163 0.666626C4.39163 0.666626 0.666626 4.39996 0.666626 8.99996C0.666626 13.6 4.39163 17.3333 8.99163 17.3333C13.6 17.3333 17.3333 13.6 17.3333 8.99996C17.3333 4.39996 13.6 0.666626 8.99163 0.666626ZM12.3333 12.3333C12.0083 12.6583 11.4833 12.6583 11.1583 12.3333L8.41663 9.59163C8.25829 9.43329 8.16663 9.22496 8.16663 8.99996V5.66663C8.16663 5.20829 8.54163 4.83329 8.99996 4.83329C9.45829 4.83329 9.83329 5.20829 9.83329 5.66663V8.65829L12.3333 11.1583C12.6583 11.4833 12.6583 12.0083 12.3333 12.3333Z", fill: "#230808" }) })),
            PaperContent: () => {
                if (value == null) {
                    const moment = dayjs();
                    onChange(moment);
                    value = moment;
                }
                return (_jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(BryxTimeSubInput, { onChange: (hour) => {
                                onChange(value.set("hour", hour));
                            }, label: "Hour", value: value.hour(), error: false }), _jsx("span", { style: colonStyling, children: ":" }), _jsx(BryxTimeSubInput, { label: "Minute", onChange: (minute) => {
                                onChange(value.set("minute", minute));
                            }, value: value.minute(), error: false }), _jsx("span", { style: colonStyling, children: ":" }), _jsx(BryxTimeSubInput, { onChange: (second) => {
                                onChange(value.set("second", second));
                            }, label: "Second", value: value.second(), error: false })] }));
            },
        }, inputFormat: "HH:mm:ss", mask: "__:__:__", acceptRegex: /\d/gi, renderInput: ({ value, inputProps, ...rest }) => (_jsx(BryxInput, { ...rest, onFocus: () => setIsFocused(true), 
            //Has to be delayed since onBlur is called before the click event and cancels the it out.
            //https://erikmartinjordan.com/onblur-prevents-onclick-react
            onBlur: () => setTimeout(() => setIsFocused(false), 60), value: value ?? "", autoComplete: "off", error: error || false, errorText: errorText, inputProps: { ...inputProps, placeholder: "HH:MM:SS" }, onChange: (e) => changeHook(e.target.value) })) }));
})(({ theme, value }) => {
    const palette = theme.palette.mode;
    const hoverColor = BryxColors.brand1(palette);
    const activeColor = palette == "light" ? BryxColors.brandDark2(palette) : BryxColors.brand4(palette);
    const inactiveColor = BryxColors.notify(palette, "inactive");
    const validationError = value == null ? false : !value.isValid();
    return {
        "& ~ .MuiFormLabel-root": {
            color: validationError ? BryxColors.notify(palette, "error") : inactiveColor,
        },
        "body:not(:has(.MuiPopperUnstyled-root)) &:hover:not(:has(.Mui-focused)) ~ .MuiFormLabel-root": {
            color: hoverColor,
        },
        "body:has(.MuiPopperUnstyled-root) & ~ .MuiFormLabel-root": {
            color: validationError ? BryxColors.notify(palette, "error") : activeColor,
        },
        "&:hover": {
            "& ~ .MuiFormLabel-root": {
                color: hoverColor,
            },
        },
        "& .MuiInputBase-root": {
            borderWidth: "1px",
            right: "1px",
            "&:not(:hover) ::placeholder": {
                color: inactiveColor,
                opacity: 1,
            },
            "&.Mui-focused ::placeholder": {
                color: activeColor,
                opacity: 1,
            },
        },
        "& .MuiInputBase-root:not(.Mui-focused)": {
            "& *": {
                fill: inactiveColor,
                borderRadius: "4px",
            },
            "&:has(input:not(:placeholder-shown)) *": {
                fill: validationError ? BryxColors.notify(palette, "error") : activeColor,
                borderRadius: "4px",
            },
            "&:hover svg": {
                fill: hoverColor,
            },
            borderWidth: "1px",
        },
        // Focus (clicked and/or open)
        "body:has(.MuiPopperUnstyled-root) & .MuiInputBase-root, body:has(.MuiPopperUnstyled-root) & .MuiInputBase-root": {
            "& *, &:hover, &:hover *": {
                color: `activeColor !important`,
                fill: `activeColor !important`,
                borderColor: `activeColor !important`,
            },
            "& ~ .MuiFormLabel-root": {
                color: `activeColor !important`,
            },
        },
        "& .MuiInputBase-root.Mui-focused, & .MuiInputBase-root.Mui-focused": {
            borderColor: `${activeColor} !important`,
            borderWidth: "1px",
            "& *": {
                borderRadius: "4px",
                "& button:hover *": {
                    fill: hoverColor,
                },
            },
        },
        // Hover, not focused
        "& .MuiInputBase-root:not(.Mui-focused):hover, & .MuiInputBase-root:not(.Mui-focused):hover *": {
            color: hoverColor,
            fill: hoverColor,
            "&::placeholder": {
                opacity: 1,
            },
            "& ~ .MuiFormLabel-root": {
                color: hoverColor,
            },
        },
        // Hover and focused
        "& .Mui-focused:hover *": {
            color: activeColor,
            fill: activeColor,
            borderColor: `${activeColor} !important`,
            borderWidth: "1px",
        },
        // change input color if it has content
        "& .MuiInputBase-root:has(input:not(:placeholder-shown))": {
            "& *": {
                color: activeColor,
            },
            "&:not(.Mui-focused):hover *, & button:hover *": {
                fill: hoverColor,
            },
        },
    };
}));
export default BryxTimeInput;
